import React, { useEffect, useState, useCallback } from "react";
import Filters from "../components/Filters";
import StatCard from "../components/StatCard";
import { LoadingCards, ErrorState } from "../components/EmptyState";
import { api, monthLabel, fmtNum } from "../lib/api";
import { Users, User, UserCircle2, Home, MapPin, TrendingUp } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  BarChart, Bar, PieChart, Pie, Cell,
} from "recharts";

function todayDefaults() {
  const now = new Date();
  const months = ["januari","februari","maret","april","mei","juni","juli","agustus","september","oktober","november","desember"];
  return { tahun: String(now.getFullYear()), bulan: months[now.getMonth()], rw: "", rt: "" };
}

const COLORS = ["#0f766e", "#34d399", "#eab308", "#ef4444", "#3b82f6"];

export default function Dashboard() {
  const [filter, setFilter] = useState({ tahun: "2026", bulan: "september", rw: "", rt: "" });
  const [rekap, setRekap] = useState(null);
  const [status, setStatus] = useState(null);
  const [rekapRw, setRekapRw] = useState(null);
  const [tren, setTren] = useState(null);
  const [trendRequested, setTrendRequested] = useState(false);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [updated, setUpdated] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const [rk, st, rrw] = await Promise.all([
        api.rekap(filter.tahun, filter.bulan),
        api.status(filter.tahun, filter.bulan),
        api.rekapRw(filter.tahun, filter.bulan),
      ]);
      setRekap(rk); setStatus(st); setRekapRw(rrw);
      setUpdated(new Date());
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [filter.tahun, filter.bulan]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setTren(null);
    setTrendRequested(false);
    setTrendError(false);
  }, [filter.tahun, filter.bulan]);

  const loadTrend = useCallback(async () => {
    setTrendRequested(true);
    setTrendLoading(true);
    setTrendError(false);

    try {
      setTren(await api.tren(filter.tahun, filter.bulan, rekap));
    } catch (e) {
      setTrendError(true);
    } finally {
      setTrendLoading(false);
    }
  }, [filter.tahun, filter.bulan, rekap]);

  const summary = rekap?.summary || {};
  const total = summary.total_penduduk || 0;
  const laki = summary.laki_laki || 0;
  const perempuan = summary.perempuan || 0;
  const tetap = summary.penduduk_tetap || 0;
  const sementara = summary.penduduk_sementara || 0;
  const sudah = status?.sudah_mengisi || 0;
  const totalRt = status?.total_rt || 75;
  const persentase = status?.persentase || 0;

  const rwChart = (rekapRw?.data || []).map((r) => ({
    rw: `RW ${r.rw}`, penduduk: r.total_penduduk, laki: r.laki_laki, perempuan: r.perempuan,
  }));
  const trenChart = (tren?.data || []).map((t) => ({
    bulan: t.bulan.slice(0, 3).toUpperCase(),
    total: t.total_penduduk,
    laporan: t.jumlah_laporan,
  }));
  const genderPie = [
    { name: "Laki-laki", value: laki },
    { name: "Perempuan", value: perempuan },
  ];
  const mutasi = [
    { name: "Kelahiran", value: summary.kelahiran || 0 },
    { name: "Kematian", value: summary.kematian || 0 },
    { name: "Pindah", value: summary.pindah || 0, },
    { name: "Datang", value: summary.datang || 0 },
  ];
  const progressChart = (rekapRw?.data || []).map((r) => ({
    rw: `RW ${r.rw}`, progress: r.progress, sudah: r.sudah_mengisi, belum: r.belum_mengisi,
  }));

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0e2b26] tracking-tight">Dashboard Utama</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            Kelurahan Suka Asih · Kota Bandung · Periode <strong className="text-[#0e2b26]">{monthLabel(filter.bulan)} {filter.tahun}</strong>
          </p>
        </div>
        {updated && (
          <div className="text-xs text-[hsl(var(--muted-foreground))]">
            Terakhir diperbarui: <span className="font-semibold text-[#0e2b26]">{updated.toLocaleTimeString("id-ID")}</span>
          </div>
        )}
      </div>

      <Filters value={filter} onChange={setFilter} onRefresh={load} />

      {loading ? (
        <LoadingCards count={6} />
      ) : error ? (
        <ErrorState onRetry={load} />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4" data-testid="stats-grid">
            <StatCard testId="stat-total" label="Total Penduduk" value={total} sub="Jiwa" icon={Users} tone="default" />
            <StatCard testId="stat-laki" label="Laki-laki" value={laki} sub="Jiwa" icon={User} tone="blue" />
            <StatCard testId="stat-perempuan" label="Perempuan" value={perempuan} sub="Jiwa" icon={UserCircle2} tone="pink" />
            <StatCard testId="stat-tetap" label="Penduduk Tetap" value={tetap} sub="Jiwa" icon={Home} tone="amber" />
            <StatCard testId="stat-sementara" label="Penduduk Sementara" value={sementara} sub="Jiwa" icon={MapPin} tone="slate" />
            <StatCard testId="stat-progress" label="Progress Pelaporan" value={`${sudah} / ${totalRt}`} sub={`${persentase}%`} icon={TrendingUp} tone="accent" />
          </div>

          <div className="bg-gradient-to-br from-[#0e2b26] to-[#16403a] text-white rounded-xl p-5 sm:p-6" data-testid="executive-summary">
            <div className="text-[0.7rem] font-bold tracking-widest text-[#7fd7bb] uppercase mb-2">Ringkasan Eksekutif</div>
            <p className="text-base sm:text-lg leading-relaxed">
              Pada periode <strong>{monthLabel(filter.bulan)} {filter.tahun}</strong>, <strong className="text-[#34d399]">{sudah} dari {totalRt} RT</strong> telah menyampaikan laporan penduduk (Format A.5).
              Total penduduk tercatat sebanyak <strong className="text-[#34d399]">{fmtNum(total)} jiwa</strong>, terdiri atas {fmtNum(laki)} laki-laki dan {fmtNum(perempuan)} perempuan.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="chart-card">
              <div className="flex items-center justify-between gap-3 mb-3">
                <h3 className="font-bold text-[#0e2b26]">Tren Perkembangan Penduduk {filter.tahun}</h3>
                {!trendRequested && (
                  <button
                    onClick={loadTrend}
                    className="text-sm font-semibold text-[#0f766e] hover:underline"
                  >
                    Muat tren
                  </button>
                )}
              </div>
              {trendLoading ? (
                <p className="py-8 text-center text-sm text-[hsl(var(--muted-foreground))]">Memuat tren tahunan...</p>
              ) : trendError ? (
                <div className="py-8 text-center">
                  <p className="mb-2 text-sm text-red-600">Tren tahunan gagal dimuat.</p>
                  <button onClick={loadTrend} className="text-sm font-semibold text-[#0f766e] hover:underline">
                    Coba lagi
                  </button>
                </div>
              ) : trendRequested ? (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={trenChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="bulan" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="total" stroke="#0f766e" strokeWidth={2.5} dot={{ r: 3 }} name="Total Penduduk" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="py-8 text-center text-sm text-[hsl(var(--muted-foreground))]">
                  Tren tahunan dimuat hanya saat diperlukan.
                </p>
              )}
            </div>

            <div className="chart-card">
              <h3 className="font-bold text-[#0e2b26] mb-3">Sebaran Penduduk per RW</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={rwChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="rw" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="penduduk" fill="#34d399" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-card">
              <h3 className="font-bold text-[#0e2b26] mb-3">Kelahiran, Kematian, Pindah & Datang</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={mutasi}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#0f766e" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-card">
              <h3 className="font-bold text-[#0e2b26] mb-3">Komposisi Laki-laki vs Perempuan</h3>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                  <Pie
                    data={genderPie}
                    innerRadius={55}
                    outerRadius={90}
                    paddingAngle={3}
                    dataKey="value"
                    labelLine={false}
                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                  >
                    <Cell fill="#0f766e" />
                    <Cell fill="#f472b6" />
                  </Pie>
                  <Tooltip formatter={(v, n) => [fmtNum(v) + " jiwa", n]} />
                  <Legend verticalAlign="bottom" height={28} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-card lg:col-span-2">
              <h3 className="font-bold text-[#0e2b26] mb-3">Progress Pelaporan per RW</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={progressChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="rw" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} unit="%" />
                  <Tooltip />
                  <Bar dataKey="progress" fill="#34d399" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
